import { Renderer, CellModel, GridModel, RowModel, renderers } from "@omegagrid/grid";
import { AxisItemProps } from "../tableAdapter";
import { dom } from "@omegagrid/core";
import type { CreateCellRendererOptions } from ".";
import { Extremes, calculateExtremes } from "./extremes";
import constants from "../../../constants";

const calculateLollipopData = (model: GridModel): Extremes => {
	const colIndices = model.columns.filter(
		c => (c.properties as AxisItemProps)?.renderer === 'lollipop'
	).map(col => col.index);
	return calculateExtremes(model, colIndices);
}

export const createLollipopRenderer = (opt: CreateCellRendererOptions): Renderer => {
	let data: Extremes = null;
	const globalExtremes = opt.scope == 'global';
	
	return (div: HTMLDivElement, cell: CellModel) => {
		const c = cell.column.index;
		// calculate all data during render of the first cell
		data ??= calculateLollipopData(cell.model);

		renderers.BaseRenderer(div, cell);
		
		const value = cell.value as number || 0;
		const w = cell.column.width;
		const h = cell.row.height;
		
		// Center is always in the middle of the cell
		const centerPos = w / 2;
		
		// Calculate symmetric range from min/max
		const maxAbsValue = globalExtremes
			? Math.max(Math.abs(data.min), Math.abs(data.max))
			: Math.max(Math.abs(data.columns.get(c).min), Math.abs(data.columns.get(c).max));
		
		// Calculate value position relative to center
		const valueOffset = (value / maxAbsValue) * (w / 2);
		const valuePos = centerPos + valueOffset;
		
		const isNegative = value < 0;
		const color = isNegative ? 'rgba(179,39,39,1)' : 'rgba(58,179,39,1)';
		
		// Create vertical zero line (gray) in the center
		const zeroLine = dom.createElement('div');
		dom.setPosition(zeroLine, {t: constants.CELL_CHART_PADDING, b: constants.CELL_CHART_PADDING, l: centerPos - 1});
		dom.setSize(zeroLine, {w: 2});
		zeroLine.style.background = '#999';
		div.appendChild(zeroLine);
		
		// Create stem (thin line from center to value)
		const stem = dom.createElement('div');
		const stemWidth = Math.abs(valuePos - centerPos);
		const stemLeft = Math.min(centerPos, valuePos);
		
		dom.setPosition(stem, {t: h / 2 - 1, l: stemLeft});
		dom.setSize(stem, {w: stemWidth, h: 3});
		stem.style.background = color;
		div.appendChild(stem);
		
		// Create lollipop circle at the value position
		const circle = dom.createElement('div');
		const circleSize = 10;
		
		dom.setPosition(circle, {t: h / 2 - circleSize / 2, l: valuePos - circleSize / 2});
		dom.setSize(circle, {w: circleSize, h: circleSize});
		circle.style.background = color;
		circle.style.borderRadius = '50%';
		circle.style.border = `2px solid ${isNegative ? 'rgba(116,25,25,1)' : 'rgba(33,88,19,1)'}`;
		div.appendChild(circle);
		
		return div;
	};
}
